import React, { useCallback, useEffect, useState } from 'react';
import BASE_URL from '../config';

const box = { color: '#e5e7eb' };
const btn = (bg = '#2a3342') => ({
  background: bg, color: '#fff', border: '1px solid #3b4658', borderRadius: 6,
  padding: '7px 14px', cursor: 'pointer', fontSize: 14, marginRight: 8,
});
const th = { textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #3b4658', color: '#9ca3af', fontSize: 12 };
const td = { padding: '6px 10px', borderBottom: '1px solid #232c3a', fontSize: 13, verticalAlign: 'top' };
const field = { width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6, border: '1px solid #3b4658', background: '#111827', color: '#e5e7eb', fontSize: 14 };

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
});
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '');

/** Ticket history or email history for one user, from helpdesk.mavistech.uk. */
export const HelpdeskPanel = ({ userId, userEmail = '', mode = 'tickets', refreshKey = 0, onCreate }) => {
  const [composeSubject, setComposeSubject] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${BASE_URL}/api/admin/helpdesk/${userId}/history/`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Could not load helpdesk data.');
      setData(await r.json());
    } catch (e) {
      setError(e.message || 'Could not load helpdesk data.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const head = (
    <div style={{ marginBottom: 12 }}>
      {onCreate && <button type="button" style={btn('#2563eb')} onClick={onCreate}>Create ticket</button>}
      {mode === 'emails' && <button type="button" style={btn('#2563eb')} onClick={() => setComposeSubject('')}>Send email</button>}
      <button type="button" style={btn()} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      {data && mode === 'tickets' && (
        <span style={{ marginLeft: 8, fontSize: 13 }}>{data.open_ticket_count} open of {data.tickets.length} ticket(s)</span>
      )}
    </div>
  );

  if (error) return <div style={box}>{head}<div role="alert" style={{ color: '#f87171' }}>{error}</div></div>;
  if (!data) return <div style={box}>{head}Loading…</div>;

  if (mode === 'tickets') {
    return (
      <div style={box}>
        {head}
        {data.tickets.length === 0 ? <div>No tickets for this user.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Ticket</th><th style={th}>Title</th><th style={th}>Status</th>
              <th style={th}>Priority</th><th style={th}>Created</th><th style={th}>Updated</th>
            </tr></thead>
            <tbody>
              {data.tickets.map((t) => (
                <tr key={t.id}>
                  <td style={td}><a href={t.url} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>{t.key}</a></td>
                  <td style={td}>{t.title}</td>
                  <td style={td}>{t.status}{t.is_completed ? ' ✓' : ''}</td>
                  <td style={td}>{t.priority}</td>
                  <td style={td}>{fmt(t.created_at)}</td>
                  <td style={td}>{fmt(t.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  }

  const rows = data.events.filter((e) => e.type !== 'ticket_created');
  return (
    <div style={box}>
      {head}
      {composeSubject !== null && (
        <SendEmailModal userId={userId} userEmail={userEmail} initialSubject={composeSubject}
          onClose={() => setComposeSubject(null)}
          onSent={() => { setComposeSubject(null); load(); }} />
      )}
      {rows.length === 0 ? <div>No emails or ticket messages for this user.</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>When</th><th style={th}>Direction</th><th style={th}>Ticket</th><th style={th}>Subject / message</th>
          </tr></thead>
          <tbody>
            {rows.map((e, i) => {
              const dir = e.type === 'email_in' || e.direction === 'customer' ? 'From customer'
                : 'To customer';
              const isEmail = e.type === 'email_in' || e.type === 'email_out';
              const text = e.body || '';
              return (
                <tr key={i}>
                  <td style={td}>{fmt(e.at)}</td>
                  <td style={td}>{dir}{isEmail ? ' (email)' : ''}{e.author ? ` · ${e.author}` : ''}</td>
                  <td style={td}>{e.ticket_key || ''}</td>
                  <td style={td}>
                    {e.subject && <div><strong>{e.subject}</strong></div>}
                    <div style={{ whiteSpace: 'pre-wrap' }}>{open[i] || text.length < 240 ? text : `${text.slice(0, 240)}…`}</div>
                    {e.type === 'email_in' && (
                      <button type="button" style={{ ...btn('#2563eb'), padding: '2px 8px', marginTop: 4, fontSize: 12 }}
                        onClick={() => setComposeSubject(/^re:/i.test(e.subject || '') ? e.subject : `Re: ${e.subject || ''}`)}>Reply</button>
                    )}
                    {text.length >= 240 && (
                      <button type="button" style={{ ...btn(), padding: '2px 8px', marginTop: 4, fontSize: 12 }}
                        onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}>{open[i] ? 'Show less' : 'Show more'}</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

/** Modal to raise a helpdesk ticket for this user. */
export const CreateTicketModal = ({ userId, userLabel, onClose, onCreated }) => {
  const [boards, setBoards] = useState([]);
  const [board, setBoard] = useState('SD');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [staff, setStaff] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/helpdesk/employees/`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((b) => setStaff(Array.isArray(b) ? b : []))
      .catch(() => setStaff([]));
    fetch(`${BASE_URL}/api/admin/helpdesk/boards/`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((b) => setBoards(Array.isArray(b) ? b : []))
      .catch(() => setBoards([]));
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { setError('Please enter a title.'); return; }
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${BASE_URL}/api/admin/helpdesk/${userId}/tickets/`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ title, description, priority, board, due_date: dueDate || null, assignee_ids: assignees }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Could not create the ticket.');
      onCreated(j);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="ct-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} style={{ background: '#1f2937', color: '#e5e7eb', borderRadius: 10, padding: 20, width: 480, maxWidth: '92vw' }}>
        <h3 id="ct-title" style={{ marginTop: 0 }}>Create ticket{userLabel ? ` for ${userLabel}` : ''}</h3>
        <label htmlFor="ct-board">Board</label>
        <select id="ct-board" style={field} value={board} onChange={(e) => setBoard(e.target.value)}>
          {(boards.length ? boards : [{ key: 'SD', name: 'Service Desk' }]).map((b) => <option key={b.key} value={b.key}>{b.name}</option>)}
        </select>
        <label htmlFor="ct-t" style={{ display: 'block', marginTop: 10 }}>Title</label>
        <input id="ct-t" style={field} value={title} maxLength={255} autoFocus onChange={(e) => setTitle(e.target.value)} />
        <label htmlFor="ct-d" style={{ display: 'block', marginTop: 10 }}>Description</label>
        <textarea id="ct-d" style={{ ...field, minHeight: 110 }} value={description} onChange={(e) => setDescription(e.target.value)} />
        <label htmlFor="ct-p" style={{ display: 'block', marginTop: 10 }}>Priority</label>
        <select id="ct-p" style={field} value={priority} onChange={(e) => setPriority(e.target.value)}>
          {['highest', 'high', 'medium', 'low', 'lowest'].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <label htmlFor="ct-due" style={{ display: 'block', marginTop: 10 }}>Due date (optional)</label>
        <input id="ct-due" type="date" style={field} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        <fieldset style={{ border: '1px solid #3b4658', borderRadius: 6, marginTop: 12, padding: '6px 10px', maxHeight: 130, overflowY: 'auto' }}>
          <legend style={{ fontSize: 13 }}>Assign to (optional)</legend>
          {staff.length === 0 && <span style={{ fontSize: 13 }}>No staff list available.</span>}
          {staff.map((s) => (
            <label key={s.id} style={{ display: 'block', fontSize: 14 }}>
              <input type="checkbox" checked={assignees.includes(s.id)}
                onChange={(e) => setAssignees((a) => (e.target.checked ? [...a, s.id] : a.filter((x) => x !== s.id)))} />{' '}{s.name}
            </label>
          ))}
        </fieldset>
        {error && <div role="alert" style={{ color: '#f87171', marginTop: 10 }}>{error}</div>}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button type="button" style={btn()} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...btn('#2563eb'), marginRight: 0 }} disabled={busy}>{busy ? 'Creating…' : 'Create ticket'}</button>
        </div>
      </form>
    </div>
  );
};

/** Modal to email the user directly; recorded in their Email History. */
export const SendEmailModal = ({ userId, userEmail, initialSubject = '', onClose, onSent }) => {
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) { setError('Please write a message.'); return; }
    setBusy(true);
    setError('');
    try {
      const r = await fetch(`${BASE_URL}/api/admin/helpdesk/${userId}/send-email/`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ subject, body }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'Could not send the email.');
      onSent(j);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="se-title"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} style={{ background: '#1f2937', color: '#e5e7eb', borderRadius: 10, padding: 20, width: 560, maxWidth: '92vw' }}>
        <h3 id="se-title" style={{ marginTop: 0 }}>Send email</h3>
        <div style={{ marginBottom: 8 }}>To: <strong>{userEmail || 'the user\'s email on file'}</strong></div>
        <label htmlFor="se-s">Subject</label>
        <input id="se-s" style={field} value={subject} maxLength={255} onChange={(e) => setSubject(e.target.value)} />
        <label htmlFor="se-b" style={{ display: 'block', marginTop: 10 }}>Message</label>
        <textarea id="se-b" style={{ ...field, minHeight: 180 }} value={body} autoFocus onChange={(e) => setBody(e.target.value)} />
        {error && <div role="alert" style={{ color: '#f87171', marginTop: 10 }}>{error}</div>}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button type="button" style={btn()} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...btn('#2563eb'), marginRight: 0 }} disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
        </div>
      </form>
    </div>
  );
};

export default HelpdeskPanel;
